import React, {useContext} from 'react';

import {ErrorFlatButton} from 'src/components/error-flat-button';
import {ChangesInfo} from 'src/modules/profile/account/tabs/admin-account-api-v2/components/changes-info';
import {getObjectEntries} from 'src/modules/profile/account/tabs/admin-account-api-v2/utils/getObjectEntries';
import {
    getLimitPeriodCaption,
    getLimitTypeCaption,
} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/controllers/limits/constants';
import {LimitType} from 'src/openapi/admin/models/LimitType';

import BoxLoading from '@pb/kit-component-box-loading';
import {Group, Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {LocaleContext} from '@pb/kit-context-locale';

import {LimitItem} from './limit-item';

import {
    apiV2LimitsList,
} from './limits.view.module.styl';

import type {LimitsMap} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/controllers/limits/services/typedef';
import type {ApiLimit} from 'src/openapi/admin/models/ApiLimit';
import type {ChangesInfo as ChangesInfoDto} from 'src/openapi/admin/models/ChangesInfo';
import type {PricingModel} from 'src/openapi/admin/models/PricingModel';

type Props = {
    limits: LimitsMap,
    changesInfo: ChangesInfoDto | null,
    busy: boolean,
    onRemoveLimit: (id: string) => void,
    onAddLimit: (limit: ApiLimit) => void,
    pricingModel: PricingModel,
    disabled?: boolean,
    contractLevel?: boolean,
};

export const LimitsView = (props: Props) => {
    const {locale} = useContext(LocaleContext);
    const {busy, changesInfo, limits, pricingModel, disabled, contractLevel} = props;

    return (
        <BoxLoading loading={busy}>
            <Group direction="v" gap="xl" alignItems="start">
                <Group gap="none" style={{maxHeight: 600}}>
                    <Group direction="v" gap="s" className={apiV2LimitsList}>
                        <Group className="pdx-s" gap="none" alignItems="center" justify="flex-start">
                            <Item style={{width: 175}}>
                                <Label>Limit By</Label>
                            </Item>
                            <Group className="pl-s" style={{width: 165}} gap="none">
                                <Item style={{width: 135}}>
                                    <Label>Quantity</Label>
                                </Item>
                                <Item>/</Item>
                                <Item style={{width: 80}} className="pl-m">
                                    <Label>Period</Label>
                                </Item>
                            </Group>
                            <Item style={{width: 14}}/>
                        </Group>
                        {getObjectEntries(limits).map(
                            ([id, limit]) => {
                                // SEARCH_AND_UPDATE is deprecated and shown read-only at contract level.
                                const readOnly = contractLevel && limit.limitType === LimitType.SEARCH_AND_UPDATE;

                                return (
                                    <Group key={id} className="pdx-s" gap="none" alignItems="center">
                                        <Item style={{width: 185}}>{getLimitTypeCaption(limit.limitType)}</Item>
                                        <Item style={{width: 90}}>{limit.quantity}</Item>
                                        <Item>/</Item>
                                        <Item style={{width: 125}} className="pl-m">
                                            {getLimitPeriodCaption(limit.period)}
                                        </Item>
                                        <Item style={{width: 14}}>
                                            {readOnly ? null : (
                                                <ErrorFlatButton
                                                    icon={Icons.REMOVE}
                                                    label="Remove"
                                                    onClick={() => {
                                                        props.onRemoveLimit(id);
                                                    }}
                                                />
                                            )}
                                        </Item>
                                    </Group>
                                );
                            },
                        )}
                        <LimitItem
                            locale={locale}
                            onAddLimit={props.onAddLimit}
                            pricingModel={pricingModel}
                            limits={limits}
                            disabled={disabled}
                            contractLevel={contractLevel}
                        />
                    </Group>
                </Group>
                {changesInfo ? (
                    <Group>
                        <ChangesInfo data={changesInfo}/>
                    </Group>
                ) : null}
            </Group>
        </BoxLoading>
    );
};
